import { useCallback, useEffect, useRef, useState } from "react";

import { getSurveyTranslations } from "@rubi/api-generated";
import { toast } from "@rubi/ui";

import { supportedLanguages } from "@/common/utils";

const BILINGUAL_NOTE_SEPARATOR_LINE = "____";
const DEFAULT_TRANSLATION_LANGUAGE = "fr";

export function splitBilingualMarkdownParts(markdown: string): {
  originalMarkdown: string;
  translatedMarkdown: string | null;
} {
  const separatorRegex = new RegExp(
    `^\\\\s*${BILINGUAL_NOTE_SEPARATOR_LINE}\\\\s*$`,
    "m",
  );
  const match = separatorRegex.exec(markdown);

  if (!match || match.index === undefined) {
    return { originalMarkdown: markdown.trimEnd(), translatedMarkdown: null };
  }

  const originalMarkdown = markdown.slice(0, match.index).trimEnd();
  const separatorEndIndex = match.index + match[0].length;
  const translatedMarkdown = markdown.slice(separatorEndIndex).trimStart();

  return {
    originalMarkdown,
    translatedMarkdown: translatedMarkdown.trim().length
      ? translatedMarkdown.trimEnd()
      : null,
  };
}

export function buildBilingualMarkdown(
  originalMarkdown: string,
  translatedText: string,
): string {
  const cleanOriginal = originalMarkdown.trimEnd();
  const cleanTranslation = translatedText.trim();
  return `${cleanOriginal}\n${BILINGUAL_NOTE_SEPARATOR_LINE}\n${cleanTranslation}`;
}

function stripMarkdownToPlainText(markdown: string): string {
  let text = markdown;
  text = text.replace(/\r\n/g, "\n");
  text = text.replace(/```[\w-]*\n([\s\S]*?)```/g, "$1");
  text = text.replace(/`([^`]*)`/g, "$1");
  text = text.replace(/!\[([^\]]*)\]\([^)]+\)/g, "$1");
  text = text.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");
  text = text.replace(/^#{1,6}\s+/gm, "");
  text = text.replace(/^\s*>\s?/gm, "");
  text = text.replace(/^\s*([-*+]|(\d+\.))\s+/gm, "");
  text = text.replace(/^\s*---\s*$/gm, "");

  text = text.replace(/\*\*(.*?)\*\*/g, "$1");
  text = text.replace(/__(.*?)__/g, "$1");
  text = text.replace(/\*(.*?)\*/g, "$1");
  text = text.replace(/_(.*?)_/g, "$1");
  text = text.replace(/~~(.*?)~~/g, "$1");
  text = text.replace(/~(.*?)~/g, "$1"); // lexical underline marker

  text = text.replace(/\s+\n/g, "\n");
  text = text.replace(/[ \t]{2,}/g, " ");
  text = text.trim();
  return text;
}

interface UseTranslationOptions {
  isModalOpen: boolean;
  specifiedReason: string;
  setSpecifiedReason: (reason: string) => void;
}

export function useActionModalTranslation({
  isModalOpen,
  specifiedReason,
  setSpecifiedReason,
}: UseTranslationOptions) {
  const hasHydratedRef = useRef(false);
  const suppressOutdatedOnceRef = useRef(false);
  const translatedTextRawRef = useRef("");

  const [translationTargetLanguageCode, setTranslationTargetLanguageCode] =
    useState("");
  const [translatedLanguageCode, setTranslatedLanguageCode] = useState<
    string | null
  >(null);
  const [translatedTextRawState, setTranslatedTextRawState] = useState("");
  const [isTranslationOutdated, setIsTranslationOutdated] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);

  const setTranslatedTextRaw = useCallback((text: string) => {
    translatedTextRawRef.current = text;
    setTranslatedTextRawState(text);
  }, []);

  useEffect(() => {
    if (!isModalOpen) return;
    if (hasHydratedRef.current) return;
    hasHydratedRef.current = true;

    const { originalMarkdown, translatedMarkdown } =
      splitBilingualMarkdownParts(specifiedReason);

    if (translatedMarkdown) {
      setTranslatedLanguageCode(DEFAULT_TRANSLATION_LANGUAGE);
      setTranslatedTextRaw(translatedMarkdown.trim());
      setIsTranslationOutdated(false);

      if (originalMarkdown.trim() !== specifiedReason.trim()) {
        suppressOutdatedOnceRef.current = true;
        setSpecifiedReason(originalMarkdown);
      }
    }
  }, [isModalOpen, specifiedReason, setSpecifiedReason, setTranslatedTextRaw]);

  useEffect(() => {
    if (!translatedLanguageCode) {
      setTranslationTargetLanguageCode("");
      return;
    }
    setTranslationTargetLanguageCode(translatedLanguageCode);
  }, [translatedLanguageCode]);

  useEffect(() => {
    if (!isModalOpen) {
      hasHydratedRef.current = false;
      suppressOutdatedOnceRef.current = false;
    }
  }, [isModalOpen]);

  const clearTranslation = useCallback(() => {
    setTranslatedLanguageCode(null);
    setTranslatedTextRaw("");
    setIsTranslationOutdated(false);
    setTranslationTargetLanguageCode("");
  }, [setTranslatedTextRaw]);

  const resetOnClose = useCallback(() => {
    clearTranslation();
    setIsTranslating(false);
    suppressOutdatedOnceRef.current = false;
    hasHydratedRef.current = false;
  }, [clearTranslation]);

  const markOutdatedUnlessSuppressed = useCallback(() => {
    if (suppressOutdatedOnceRef.current) {
      suppressOutdatedOnceRef.current = false;
      return;
    }
    setIsTranslationOutdated(true);
  }, []);

  /**
   * Translates the source markdown into the target language.
   * Accepts the full editor markdown (including any existing translation block)
   * and handles splitting, stripping, and API communication internally.
   */
  const translate = useCallback(
    async (sourceMarkdown: string, languageCode: string) => {
      setIsTranslating(true);
      setIsTranslationOutdated(false);

      try {
        const { originalMarkdown } =
          splitBilingualMarkdownParts(sourceMarkdown);
        const plainForTranslation = stripMarkdownToPlainText(originalMarkdown);

        if (!plainForTranslation.trim()) {
          toast.warning("No text to translate.");
          return;
        }

        const response = await getSurveyTranslations({
          body: {
            source_strings: [{ text: plainForTranslation }],
            target_language: languageCode,
          },
        });

        const translatedText =
          response.data?.translations?.[0]?.translations?.[0]?.text;

        if (!translatedText) {
          toast.error("Translation failed. Please try again.");
          return;
        }

        setTranslatedLanguageCode(languageCode);
        setTranslatedTextRaw(translatedText.trim());
        suppressOutdatedOnceRef.current = true;
        setIsTranslationOutdated(false);
      } catch (error) {
        console.error("Translation failed:", error);
        toast.error("Translation failed. Please try again.");
      } finally {
        setIsTranslating(false);
      }
    },
    [setTranslatedTextRaw],
  );

  const onTranslatedEditorChange = useCallback(
    (markdown: string) => {
      const next = markdown.trim();
      if (next === translatedTextRawRef.current.trim()) return;
      setTranslatedTextRaw(next);
    },
    [setTranslatedTextRaw],
  );

  const hasTranslation = translatedLanguageCode !== null;

  const translatedLanguageName = translatedLanguageCode
    ? (supportedLanguages.find((lang) => lang.code === translatedLanguageCode)
        ?.name ?? translatedLanguageCode.toUpperCase())
    : "";

  return {
    translationTargetLanguageCode,
    setTranslationTargetLanguageCode,
    translatedLanguageCode,
    translatedTextRaw: translatedTextRawState,
    isTranslationOutdated,
    isTranslating,
    hasTranslation,
    translatedLanguageName,
    translate,
    clearTranslation,
    resetOnClose,
    markOutdatedUnlessSuppressed,
    onTranslatedEditorChange,
  };
}
